import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { SET_NOTIFICATION_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler.ts'
import styles from './notificationSuggest.module.scss'
import validator from 'validator'

interface EditContactEmailDialogProps {
    open: boolean
    handleClose: () => void
    daycareId: number
    currentEmails: string[]
    notification: boolean
    emailToEdit: string
    onSuccess: (newEmails: string[]) => void
}

const EditContactEmailDialog: FunctionComponent<EditContactEmailDialogProps> = ({
    open,
    handleClose,
    daycareId,
    currentEmails,
    notification,
    emailToEdit,
    onSuccess
}: EditContactEmailDialogProps) => {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [value, setValue] = useState<string>(emailToEdit)

    useEffect(() => {
        setValue(emailToEdit)
    }, [emailToEdit])

    const updatedEmails: string[] = currentEmails.map((e) => (e === emailToEdit ? value : e))

    const [editContactEmail] = useMutation(SET_NOTIFICATION_SUGGEST_SETTINGS, {
        variables: { daycareId, contactEmails: updatedEmails, notification },
        onCompleted: () => {
            onSuccess(updatedEmails)
            handleClose()
            showNotificationSuccess(t('contactEmailUpdated'))
        },
        onError: () => {
            showNotificationError(t('contactEmailUpdatedError'))
        }
    })

    const isValid: boolean =
        validator.isEmail(value) &&
        value !== emailToEdit &&
        !currentEmails.filter((e) => e !== emailToEdit).includes(value)

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('editContactEmail')} />
            <DialogBody className={styles.DialogBody}>
                <UserInput
                    label={t('email')}
                    value={value}
                    setValue={setValue}
                    type={'text'}
                    unallowedCharacters={'email'}
                    className={styles.UserInput}
                    fullWidth
                />
                <div className={styles.ButtonContainer}>
                    <ButtonTextIcon type={'close'} onClick={handleClose} className={styles.Button} />
                    <ButtonTextIcon
                        type={'confirm'}
                        disabled={!isValid}
                        onClick={() => editContactEmail()}
                        className={styles.Button}
                    />
                </div>
            </DialogBody>
        </Dialog>
    )
}

export default EditContactEmailDialog
